import io, { type Socket } from 'socket.io-client'
import { ElLoading, ElMessage, ElMessageBox } from 'element-plus'
import type { SocketEmitAsyncParams, SocketEmitParams, SocketOnParams } from './types'
import type { Res } from '@/types'
import { useUserStore } from '@/stores/modules/user'
import type { UserInfoType } from '@/types/user'
import type { EquipCollectionType, GetDarkGoldFactorRes, SupremeAffixCollectionType } from '@/types/equip'
import { setEquipCollection } from '@/core/equip'
import { useCommonStore } from '@/stores/modules/common'
import { setRuneData } from '@/core/rune'
import type { GetRuneDataRes, RuneConllectionType } from '@/types/rune'
import { useRoomStore } from '@/stores/modules/room'
import { setdarkGoldFactor } from '@/core/equip/dark-gold'
import type { RelicCollectionType } from '@/types/relic'
import { setRelicData } from '@/core/relic'
import { getMaintainStatusApi } from '@/api'
import { setSupremeAffixCollection, setSupremeBasicFactor } from '@/core/equip/supreme'
import type { GetSkillFactorRes, SkillCollectionType } from '../../types/skill'
import { setSkillData, setSkillFactor } from '@/core/skill'

export let socket: Socket | null = null

export function initSocket() {
  return new Promise((resolve, reject) => {
    let loading: any = ElLoading.service({
      lock: true,
      text: '正在连接服务器...',
      background: 'rgba(0, 0, 0, 0.7)',
    })
    socket = io(import.meta.env.VITE_WS_BASE_URL, {
      path: import.meta.env.VITE_WS_BASE_PATH,
      extraHeaders: {
        token: localStorage.getItem(import.meta.env.VITE_TOKEN_NAME) || '',
      },
      reconnection: true, // 允许重连
      reconnectionAttempts: 5, // 最大重连次数
      reconnectionDelay: 1000, // 初始重连间隔时间（单位毫秒）
      reconnectionDelayMax: 5000, // 最大重连间隔时间（单位毫秒）
      timeout: 10000, // 超时时间（单位毫秒）
    })

    socket.on('connect', async () => {
      socketEmit<EquipCollectionType>({
        type: 'getEquipCollection',
        callBack: (res) => {
          setEquipCollection(res.data)
        },
      })
      socketEmit<GetRuneDataRes>({
        type: 'getRuneData',
        callBack: (res) => {
          setRuneData(res.data)
        },
      })
      socketEmit<RelicCollectionType>({
        type: 'getRelicData',
        callBack: (res) => {
          setRelicData(res.data)
        },
      })
      socketEmit<GetDarkGoldFactorRes>({
        type: 'getDarkGoldFactor',
        callBack: (res) => {
          setdarkGoldFactor(res.data)
        },
      })
      socketEmit<SupremeAffixCollectionType>({
        type: 'getSupremeAffixCollection',
        callBack: (res) => {
          setSupremeAffixCollection(res.data)
        },
      })
      socketEmit<SupremeAffixCollectionType>({
        type: 'getSupremeBasicFactor',
        callBack: (res) => {
          setSupremeBasicFactor(res.data)
        },
      })
      socketEmit<SkillCollectionType>({
        type: 'getSkillCollection',
        callBack: (res) => {
          setSkillData(res.data)
        },
      })
      socketEmit<GetSkillFactorRes>({
        type: 'getSkillFactor',
        callBack: (res) => {
          setSkillFactor(res.data)
        },
      })
      const userInfo = await socketEmitAsync<UserInfoType>({ type: 'init' }).catch(() => loading?.close())
      if (!userInfo) return reject('fail')
      useUserStore().$state = userInfo
      resolve('suc')

      loading?.close()
      loading = null

      ElMessage.success('连接成功')
    })

    socket.on('disconnect', (reason) => {
      useCommonStore().isSocketReconnect = false
      if (reason === 'io server disconnect') {
        socket?.disconnect()
        // 服务端主动断开
        ElMessageBox.alert('连接中断，点击按钮重启游戏', '温馨提示', {
          confirmButtonText: '好的',
          beforeClose: () => {
            location.reload()
          },
        })
      } else {
        // 网络问题导致的断开
        ElMessage.error('网络连接中断')
      }
    })

    socket.io.on('reconnect', async () => {
      const res = await getMaintainStatusApi()
      if (res === 'true') {
        socket?.disconnect()
        ElMessageBox.alert('服务器维护，请耐心等待！', '温馨提示', {
          confirmButtonText: '好的',
          beforeClose: () => {
            location.reload()
          },
        })
      } else {
        ElMessage.success('重连成功')
        // 重连成功操作，同时会触发connect
        useCommonStore().isSocketReconnect = true
      }
    })
    socket.io.on('reconnect_attempt', () => {
      useCommonStore().isSocketReconnect = false
      loading = ElLoading.service({
        lock: true,
        text: '尝试重连服务器...',
        background: 'rgba(0, 0, 0, 0.7)',
      })
    })

    socket.on('connect_error', (error) => {
      reject('fail')
      loading?.close()
      loading = null
      socket?.disconnect()
      ElMessageBox.confirm(`网络连接异常，错误：${error}`, '温馨提示', {
        icon: 'warning',
        cancelButtonText: '重启游戏',
        confirmButtonText: '尝试重连',
        confirmButtonClass: 'is-plain',
        beforeClose: (action, _instance, done) => {
          if (action === 'confirm') {
            socket?.connect()
            done()
          } else {
            location.reload()
          }
        },
      })
    })

    socket.on('exception', (error) => {
      ElMessage.error(`服务器错误，错误信息：${error.message}`)
    })

    socketOn({
      type: 'repeatLogin',
      callBack: () => {
        socket?.disconnect()
        ElMessageBox.alert('您的账号已在别处登录！').then(() => {
          location.reload()
        })
      },
    })
    socketOn({
      type: 'serverMaintain',
      callBack: () => {
        socket?.disconnect()
        ElMessageBox.alert('服务器维护，请耐心等待！', {
          beforeClose() {
            location.reload()
          },
        })
      },
    })
    // 初始化房间监听方法
    useRoomStore().socketOnInit()
  })
}

export function socketEmit<T>(config: SocketEmitParams<T>): Socket {
  if (!socket) throw new Error('Socket Not Found')
  const { type, data = {}, callBack } = config
  return socket.emit(type, data, callBack)
}

const EMIT_INTERVAL = 1000
const EMIT_INTERVAL_COUNT = 15
let emitTimestamp = 0
let emitCount = 0
export async function socketEmitAsync<T>(config: SocketEmitAsyncParams<T>): Promise<T> {
  const now = Date.now()
  const { type, data = {}, limit = true, hideLoading = false } = config

  if (limit) {
    if (emitTimestamp + EMIT_INTERVAL <= now) emitCount = 0
    else emitCount++
    if (emitCount >= EMIT_INTERVAL_COUNT) {
      ElMessage.warning('操作过于频繁，休息一下吧')
      throw new Error()
    }
    emitTimestamp = now
  }

  let loading: any
  let timer: NodeJS.Timeout | null
  if (!hideLoading) {
    timer = setTimeout(() => {
      loading = ElLoading.service({
        lock: true,
        text: '连接中...',
        background: 'rgba(0, 0, 0, 0.7)',
      })
    }, 200)
  }
  return new Promise((resolve, reject) => {
    socket?.emit(type, data, (res: Res<T>) => {
      const { code, data, message } = res
      timer && clearTimeout(timer)
      timer = null
      loading?.close()
      if (code === 200) {
        if (message) ElMessage.success(message)
        resolve(data)
      } else {
        ElMessage.error(message || '服务器异常')
        reject(message)
        throw new Error(message)
      }
    })
  })
}

export function socketOn<T>(config: SocketOnParams<T>): Socket {
  if (!socket) throw new Error('Socket Not Found')
  const { type, callBack } = config
  return socket.on(type, callBack)
}

export function socketOff(type: string) {
  if (!socket) throw new Error('Socket Not Found')
  socket.off(type)
}
